import { nanoid } from "@reduxjs/toolkit";
import type {
  IToAppPayload,
  IFromAppPayload,
  MOBILE_APP_ENDPOINT,
  MobileAppSubscribe,
} from "../types";

export function sendAppMessage<T extends MOBILE_APP_ENDPOINT>(
  endpoint: T,
  payload: Omit<IToAppPayload<T>, "id" | "type">,
): Promise<void>;

export function sendAppMessage<T extends MOBILE_APP_ENDPOINT>(
  endpoint: T,
  payload: Omit<IToAppPayload<T>, "id" | "type">,
  options: {
    timeout?: number | false;
    subscribe: MobileAppSubscribe;
  },
): Promise<IFromAppPayload<T>>;

export function sendAppMessage<T extends MOBILE_APP_ENDPOINT>(
  endpoint: T,
  payload: Omit<IToAppPayload<T>, "id" | "type">,
  options?: {
    timeout?: number | false;
    subscribe?: MobileAppSubscribe;
  },
): Promise<void | IFromAppPayload<T>> {
  const _payload: IToAppPayload<T> = {
    ...(payload as IToAppPayload<T>),
    id: nanoid(),
    type: endpoint,
  };

  return new Promise((resolve, reject) => {
    if (!window.AppBridge && !window.ReactNativeWebView) {
      return reject("AppBridge is not available on window");
    }

    if (options?.subscribe) {
      const cleanup = () => {
        clearTimeout(timeout);
        unsubscribe();
      };

      const unsubscribe = options.subscribe(endpoint, (response) => {
        if (response.id !== _payload.id) {
          return;
        }

        cleanup();
        resolve(response as IFromAppPayload<T>);
      });

      let timeout: NodeJS.Timeout;
      if (options?.timeout !== false) {
        timeout = setTimeout(() => {
          cleanup();
          reject("sendAppMessage timed out");
        }, options?.timeout || 2500);
      }
    }

    // Send to Flutter and to react native jic
    if (window.AppBridge) {
      console.log("📤 sendAppMessage → AppBridge.postMessage:", _payload);
      window.AppBridge.postMessage(JSON.stringify(_payload));
    }
    if (window.ReactNativeWebView) {
      console.log(
        "📤 sendAppMessage → ReactNativeWebView.postMessage:",
        _payload,
      );
      window.ReactNativeWebView.postMessage(JSON.stringify(_payload));
    }

    if (!options?.subscribe) {
      resolve();
    }
  });
}
